import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mutate } from 'swr';
import { getImageMetadataIdsFromCustomFields } from 'components/commercetools-ui/organisms/cart/helpers';
import useRefreshImages from 'helpers/hooks/useRefreshImages';
import { OriginalPhotoInfo, ImageOptions, setupImageOptions, getBuiltUrl } from 'helpers/imageData';
import {
  getCustomizationData,
  subscribeToCustomizationData,
  updateCustomizationData,
} from 'helpers/utils/customizationStorage';
import { useProjectSettings, useWishlist } from 'frontastic';
import useCart from 'frontastic/hooks/useCart';

const WISHLIST_ACTION_KEY = '/action/wishlist/getWishlist';

export type CartLineItemRef = {
  cartId: string;
  lineItemId: string;
  distributionChannelKey: string;
};

export type WishlistItemRef = {
  wishlistId: string;
  lineItemId: string;
};

export type MyPhotoItem = {
  id: string;
  thumbnailSrc: string;
  gridSrc: string;
  previewSrc: string;
  width?: number;
  height?: number;
  mimeType?: string;
  savedAt?: string;
  alt: string;
  cartLineItemRefs: CartLineItemRef[];
  wishlistItemRefs: WishlistItemRef[];
};

export type RemovePhotosResult = { success: boolean };

export type UseMyPhotosProps = {
  photos: MyPhotoItem[];
  isLoading: boolean;
  removePhoto: (photoId: string) => Promise<RemovePhotosResult>;
  removeAllPhotos: () => Promise<RemovePhotosResult>;
};

export type UseMyPhotosOptions = {
  scope?: 'full' | 'sessionOnly';
};

const PREVIEW_DERIVED_SIZE = 1400;
/**
 * Grid tiles render at ~210px (modal) / ~320px (drawer), so 700 covers the widest retina case
 * with headroom. They used to reuse the 1400px preview derivative, which made simply opening
 * the widget trigger a full-size server-side render per photo — six times larger than anything
 * the grid displays, and slow enough to queue ahead of the preview the user actually wants.
 */
const GRID_DERIVED_SIZE = 700;
/** Film-strip thumbnails render at 62px. */
const THUMBNAIL_DERIVED_SIZE = 400;
const REMOVE_LINE_ITEM_QUANTITY = 0;

/** Fallback intrinsic dimensions (4:3) used for `next/image` when a photo has no width/height. */
export const DEFAULT_PHOTO_WIDTH = 1200;
export const DEFAULT_PHOTO_HEIGHT = 900;

type UpdateCartLineItemsPayload = {
  cartId: string;
  distributionChannelKey: string;
  cartItems: { lineItemId: string; quantity: number }[];
};

/** A cart line item can be linked to a photo via `imageMetadata.id` and/or `custom.fields.imageMetadataIds`. */
const getCartLineItemPhotoIds = (lineItem: any): string[] => {
  const photoIds = new Set<string>();

  const imageMetadataId = lineItem?.imageMetadata?.id;
  if (imageMetadataId) photoIds.add(imageMetadataId);

  for (const photoId of getImageMetadataIdsFromCustomFields(lineItem)) {
    photoIds.add(photoId);
  }

  return [...photoIds];
};

const getCartLineItemRefsByPhotoId = (miniCarts: any[]): Record<string, CartLineItemRef[]> => {
  const cartLineItemRefsByPhotoId: Record<string, CartLineItemRef[]> = {};
  const dedupeLineItemKeysByPhotoId: Record<string, Set<string>> = {};

  for (const cart of miniCarts ?? []) {
    const cartId = cart?.cartId;
    for (const lineItem of cart?.lineItems ?? []) {
      const lineItemId = lineItem?.lineItemId;
      const distributionChannelKey = lineItem?.distributionChannel?.key;
      if (!cartId || !lineItemId || !distributionChannelKey) continue;

      for (const photoId of getCartLineItemPhotoIds(lineItem)) {
        const key = `${cartId}:${lineItemId}:${distributionChannelKey}`;
        let seenRefKeys = dedupeLineItemKeysByPhotoId[photoId];
        if (!seenRefKeys) {
          seenRefKeys = new Set<string>();
          dedupeLineItemKeysByPhotoId[photoId] = seenRefKeys;
        }

        if (seenRefKeys.has(key)) continue;

        seenRefKeys.add(key);
        if (!cartLineItemRefsByPhotoId[photoId]) {
          cartLineItemRefsByPhotoId[photoId] = [];
        }

        cartLineItemRefsByPhotoId[photoId].push({ cartId, lineItemId, distributionChannelKey });
      }
    }
  }

  return cartLineItemRefsByPhotoId;
};

const getWishlistItemRefsByPhotoId = (wishlist: any): Record<string, WishlistItemRef[]> => {
  const wishlistItemRefsByPhotoId: Record<string, WishlistItemRef[]> = {};
  const dedupeLineItemKeysByPhotoId: Record<string, Set<string>> = {};

  const wishlistId = wishlist?.wishlistId;
  if (!wishlistId) return wishlistItemRefsByPhotoId;

  for (const lineItem of wishlist?.lineItems ?? []) {
    const lineItemId = lineItem?.lineItemId;
    if (!lineItemId) continue;

    const photoIds = getImageMetadataIdsFromCustomFields(lineItem);
    if (photoIds.length === 0) continue;

    const key = `${wishlistId}:${lineItemId}`;

    for (const photoId of photoIds) {
      let seenRefKeys = dedupeLineItemKeysByPhotoId[photoId];
      if (!seenRefKeys) {
        seenRefKeys = new Set<string>();
        dedupeLineItemKeysByPhotoId[photoId] = seenRefKeys;
      }

      if (seenRefKeys.has(key)) continue;

      seenRefKeys.add(key);
      if (!wishlistItemRefsByPhotoId[photoId]) {
        wishlistItemRefsByPhotoId[photoId] = [];
      }

      wishlistItemRefsByPhotoId[photoId].push({ wishlistId, lineItemId });
    }
  }

  return wishlistItemRefsByPhotoId;
};

const parseTimestamp = (value?: string): number => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isNaN(parsed) ? NaN : parsed;
};

/** Records the newest parsed `lastModifiedAt` per photo across every line item it's attached to. */
const collectLatestModified = (
  latestModifiedByPhotoId: Record<string, string>,
  photoIds: string[],
  lastModifiedAt?: string,
): void => {
  const time = parseTimestamp(lastModifiedAt);
  if (Number.isNaN(time) || !lastModifiedAt) return;

  for (const photoId of photoIds) {
    const existing = latestModifiedByPhotoId[photoId];
    if (!existing || time > parseTimestamp(existing)) {
      latestModifiedByPhotoId[photoId] = lastModifiedAt;
    }
  }
};

const getLatestModifiedByPhotoId = (miniCarts: any[], wishlist: any): Record<string, string> => {
  const latestModifiedByPhotoId: Record<string, string> = {};

  for (const cart of miniCarts ?? []) {
    for (const lineItem of cart?.lineItems ?? []) {
      collectLatestModified(latestModifiedByPhotoId, getCartLineItemPhotoIds(lineItem), lineItem?.lastModifiedAt);
    }
  }

  for (const lineItem of wishlist?.lineItems ?? []) {
    collectLatestModified(
      latestModifiedByPhotoId,
      getImageMetadataIdsFromCustomFields(lineItem),
      lineItem?.lastModifiedAt,
    );
  }

  return latestModifiedByPhotoId;
};

const buildMyPhotoItem = (
  photo: OriginalPhotoInfo,
  cartLineItemRefsByPhotoId: Record<string, CartLineItemRef[]>,
  wishlistItemRefsByPhotoId: Record<string, WishlistItemRef[]>,
  imageOptions: ImageOptions,
  latestModifiedByPhotoId: Record<string, string>,
): MyPhotoItem => {
  return {
    id: photo.id,
    thumbnailSrc: getBuiltUrl(photo, imageOptions, THUMBNAIL_DERIVED_SIZE),
    gridSrc: getBuiltUrl(photo, imageOptions, GRID_DERIVED_SIZE),
    previewSrc: getBuiltUrl(photo, imageOptions, PREVIEW_DERIVED_SIZE),
    width: photo.width,
    height: photo.height,
    savedAt: latestModifiedByPhotoId[photo.id] ?? photo?.expirationDate,
    alt: photo.id,
    cartLineItemRefs: cartLineItemRefsByPhotoId[photo.id] ?? [],
    wishlistItemRefs: wishlistItemRefsByPhotoId[photo.id] ?? [],
  };
};

const getSessionPhotos = (
  sessionPhotoInfos: OriginalPhotoInfo[],
  cartLineItemRefsByPhotoId: Record<string, CartLineItemRef[]>,
  wishlistItemRefsByPhotoId: Record<string, WishlistItemRef[]>,
  imageOptions: ImageOptions,
  latestModifiedByPhotoId: Record<string, string>,
): MyPhotoItem[] => {
  const seenPhotoIds = new Set<string>();

  const dedupedPhotos = sessionPhotoInfos.reduce<MyPhotoItem[]>((photos, photo) => {
    const item = buildMyPhotoItem(
      photo,
      cartLineItemRefsByPhotoId,
      wishlistItemRefsByPhotoId,
      imageOptions,
      latestModifiedByPhotoId,
    );

    if (item && !seenPhotoIds.has(item.id)) {
      seenPhotoIds.add(item.id);
      photos.push(item);
    }

    return photos;
  }, []);

  return sortPhotosBySavedAtDesc(dedupedPhotos);
};

const sortPhotosBySavedAtDesc = (photos: MyPhotoItem[]): MyPhotoItem[] => {
  return photos
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const parsedA = a.item.savedAt ? Date.parse(a.item.savedAt) : NaN;
      const parsedB = b.item.savedAt ? Date.parse(b.item.savedAt) : NaN;
      const timeA = Number.isNaN(parsedA) ? -Infinity : parsedA;
      const timeB = Number.isNaN(parsedB) ? -Infinity : parsedB;

      return timeB - timeA || a.index - b.index;
    })
    .map(({ item }) => item);
};

const removePhotoInfosFromSession = (photoIds: Set<string>): void => {
  updateCustomizationData((current) => {
    const photoInfos = Array.isArray(current?.photoInfos) ? current.photoInfos : [];

    return {
      ...current,
      photoInfos: photoInfos.filter((photo: OriginalPhotoInfo) => {
        const photoId = photo?.id;
        return !photoId || !photoIds.has(photoId);
      }),
    };
  });
};

const getPhotos = (
  miniCarts: any[],
  wishlist: any,
  imageOptions: ImageOptions,
  refreshedPhotoInfos: OriginalPhotoInfo[] = [],
): MyPhotoItem[] => {
  const customizationData = getCustomizationData<{ photoInfos?: OriginalPhotoInfo[] }>();
  const sessionPhotoInfos = Array.isArray(customizationData?.photoInfos) ? customizationData.photoInfos : [];
  const photoInfos =
    refreshedPhotoInfos.length > 0 ? [...refreshedPhotoInfos, ...sessionPhotoInfos] : sessionPhotoInfos;

  const cartLineItemRefsByPhotoId = getCartLineItemRefsByPhotoId(miniCarts);
  const wishlistItemRefsByPhotoId = getWishlistItemRefsByPhotoId(wishlist);
  const latestModifiedByPhotoId = getLatestModifiedByPhotoId(miniCarts, wishlist);
  return getSessionPhotos(
    photoInfos,
    cartLineItemRefsByPhotoId,
    wishlistItemRefsByPhotoId,
    imageOptions,
    latestModifiedByPhotoId,
  );
};

const getReferencedPhotoIds = (miniCarts: any[], wishlist: any): string[] => {
  const photoIds = new Set<string>();

  for (const cart of miniCarts ?? []) {
    for (const lineItem of cart?.lineItems ?? []) {
      for (const photoId of getCartLineItemPhotoIds(lineItem)) photoIds.add(photoId);
    }
  }

  for (const lineItem of wishlist?.lineItems ?? []) {
    for (const photoId of getImageMetadataIdsFromCustomFields(lineItem)) photoIds.add(photoId);
  }

  return [...photoIds];
};

const getSessionPhotoIds = (): Set<string> => {
  const customizationData = getCustomizationData<{ photoInfos?: OriginalPhotoInfo[] }>();
  const sessionPhotoInfos = Array.isArray(customizationData?.photoInfos) ? customizationData.photoInfos : [];

  const photoIds = new Set<string>();
  for (const photo of sessionPhotoInfos) {
    const photoId = photo?.id;
    if (photoId) photoIds.add(photoId);
  }

  return photoIds;
};

const getPhotoIdsSharingLineItems = (
  miniCarts: any[],
  wishlist: any,
  cartLineItemRefs: CartLineItemRef[],
  wishlistItemRefs: WishlistItemRef[],
): Set<string> => {
  const siblingPhotoIds = new Set<string>();

  const targetCartLineItemKeys = new Set(cartLineItemRefs.map((ref) => `${ref.cartId}:${ref.lineItemId}`));
  if (targetCartLineItemKeys.size > 0) {
    for (const cart of miniCarts ?? []) {
      const cartId = cart?.cartId;
      for (const lineItem of cart?.lineItems ?? []) {
        const lineItemId = lineItem?.lineItemId;
        if (!cartId || !lineItemId || !targetCartLineItemKeys.has(`${cartId}:${lineItemId}`)) continue;

        for (const photoId of getCartLineItemPhotoIds(lineItem)) siblingPhotoIds.add(photoId);
      }
    }
  }

  const targetWishlistLineItemIds = new Set(wishlistItemRefs.map((ref) => ref.lineItemId));
  if (targetWishlistLineItemIds.size > 0) {
    for (const lineItem of wishlist?.lineItems ?? []) {
      const lineItemId = lineItem?.lineItemId;
      if (!lineItemId || !targetWishlistLineItemIds.has(lineItemId)) continue;

      for (const photoId of getImageMetadataIdsFromCustomFields(lineItem)) siblingPhotoIds.add(photoId);
    }
  }

  return siblingPhotoIds;
};

const setRefreshedPhotos = (refreshedPhotos: Map<string, OriginalPhotoInfo>, files: OriginalPhotoInfo[]): void => {
  for (const file of files) {
    if (!file?.id || !file?.url) continue;

    const existingPhoto = refreshedPhotos.get(file.id);
    if (!existingPhoto || existingPhoto.url !== file.url) {
      refreshedPhotos.set(file.id, { ...existingPhoto, ...file });
    }
  }
};

const buildUpdateCartLineItemsPayloads = (lineItemRefs: CartLineItemRef[]): UpdateCartLineItemsPayload[] => {
  const payloadsByCartAndChannel = new Map<string, UpdateCartLineItemsPayload>();

  for (const ref of lineItemRefs) {
    const groupKey = `${ref.cartId}:${ref.distributionChannelKey}`;

    if (!payloadsByCartAndChannel.has(groupKey)) {
      payloadsByCartAndChannel.set(groupKey, {
        cartId: ref.cartId,
        distributionChannelKey: ref.distributionChannelKey,
        cartItems: [],
      });
    }

    payloadsByCartAndChannel.get(groupKey)?.cartItems.push({
      lineItemId: ref.lineItemId,
      quantity: REMOVE_LINE_ITEM_QUANTITY,
    });
  }

  return Array.from(payloadsByCartAndChannel.values());
};

const useMyPhotos = ({ scope = 'full' }: UseMyPhotosOptions = {}): UseMyPhotosProps => {
  const { miniCarts, updateLineItems, removeAllLineItems, miniCartsLoaded } = useCart();
  const { data: wishlist, removeLineItem } = useWishlist();
  const { projectSettings } = useProjectSettings();
  const { refreshImagesByIds } = useRefreshImages();

  const [sessionVersion, setSessionVersion] = useState(0);
  const [pendingRefreshCount, setPendingRefreshCount] = useState(0);
  
  /** Photos fetched from the host but never written to session storage. */
  const refreshedPhotosRef = useRef<Map<string, OriginalPhotoInfo>>(new Map());
  /** Photo IDs already fetched (or attempted) — keeps us from re-fetching the same ID forever. */
  const skippedPhotoIdsRef = useRef<Set<string>>(new Set());
  /** Photo IDs the user removed — keeps them from silently reappearing. */
  const removedPhotoIdsRef = useRef<Set<string>>(new Set());

  const isLoading = !miniCartsLoaded || (scope === 'full' && pendingRefreshCount > 0);

  const refreshFromSession = useCallback(() => setSessionVersion((version) => version + 1), []);

  const imageOptions = useMemo(() => setupImageOptions(projectSettings), [projectSettings]);

  const photos = useMemo(() => {
    const refreshedPhotosInfos = Array.from(refreshedPhotosRef.current.values());
    return getPhotos(miniCarts ?? [], wishlist, imageOptions, refreshedPhotosInfos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniCarts, wishlist, sessionVersion, imageOptions]);

  /**
   * Fetches photos referenced by the cart/wishlist but missing from session storage, from the host.
   */
  const refreshReferencedPhotos = useCallback(async () => {
    const referencedPhotoIds = getReferencedPhotoIds(miniCarts ?? [], wishlist);
    const sessionPhotoIds = getSessionPhotoIds();

    const photoIdsToFetch = referencedPhotoIds.filter(
      (photoId) =>
        !sessionPhotoIds.has(photoId) &&
        !skippedPhotoIdsRef.current.has(photoId) &&
        !removedPhotoIdsRef.current.has(photoId),
    );

    if (photoIdsToFetch.length === 0) {
      return;
    }

    for (const photoId of photoIdsToFetch) skippedPhotoIdsRef.current.add(photoId);

    setPendingRefreshCount((count) => count + 1);

    try {
      const refreshedPhotos = await refreshImagesByIds(photoIdsToFetch);
      const filteredRefreshedPhotos = Array.isArray(refreshedPhotos)
        ? refreshedPhotos.filter((file) => file?.id && !removedPhotoIdsRef.current.has(file.id))
        : [];
      if (filteredRefreshedPhotos.length > 0) {
        setRefreshedPhotos(refreshedPhotosRef.current, filteredRefreshedPhotos);
        refreshFromSession();
      }
    } catch {
      // Ignore refresh errors — the user can still remove photos, and the next refresh attempt
    } finally {
      setPendingRefreshCount((count) => Math.max(0, count - 1));
    }
  }, [miniCarts, wishlist, refreshImagesByIds, refreshFromSession]);

  /**
   * Removes a single photo: zeroes out its quantity on every cart line item it's attached to,
   * removes its wishlist line items (including any wishlist draft created as a side effect of
   * the cart update), then drops the photo from session storage. Missing photos are treated as
   * already removed.
   */
  const removePhoto = useCallback(
    async (photoId: string): Promise<RemovePhotosResult> => {
      const photo = photos.find((photo) => photo.id === photoId);

      if (!photo) {
        return { success: true };
      }

      try {
        const payloads = buildUpdateCartLineItemsPayloads(photo.cartLineItemRefs);

        // Remove all cart line items first.
        for (const payload of payloads) {
          await updateLineItems({
            cartId: payload.cartId,
            distributionChannelKey: payload.distributionChannelKey,
            cartItems: payload.cartItems,
          });
        }

        // Remove wishlist items we already know about.
        const removedWishlistLineItemIds = new Set<string>();

        if (photo.wishlistItemRefs.length > 0 && wishlist) {
          for (const ref of photo.wishlistItemRefs) {
            if (!ref.lineItemId) {
              continue;
            }

            await removeLineItem(wishlist, {
              lineItemId: ref.lineItemId,
            });

            removedWishlistLineItemIds.add(ref.lineItemId);
          }
        }

        // Cart removal can create new Saved Drafts.
        // Refetch only when we actually removed something from the cart.
        if (payloads.length > 0) {
          const revalidated: any = await mutate(WISHLIST_ACTION_KEY);
          const freshWishlist = revalidated?.isError ? undefined : revalidated?.data;

          for (const lineItem of freshWishlist?.lineItems ?? []) {
            const lineItemId = lineItem?.lineItemId;
            if (!lineItemId || removedWishlistLineItemIds.has(lineItemId)) {
              continue;
            }
            if (!getImageMetadataIdsFromCustomFields(lineItem).includes(photoId)) {
              continue;
            }

            await removeLineItem(freshWishlist, { lineItemId });
            removedWishlistLineItemIds.add(lineItemId);
          }
        }
      } catch {
        return { success: false };
      }

      const sessionPhotoIds = getSessionPhotoIds();
      const photoIdsSharingLineItems = getPhotoIdsSharingLineItems(
        miniCarts ?? [],
        wishlist,
        photo.cartLineItemRefs,
        photo.wishlistItemRefs,
      );
      const nonSessionPhotoIds = [...photoIdsSharingLineItems].filter(
        (id) => id !== photo.id && !sessionPhotoIds.has(id),
      );
      const removedPhotoIds = new Set([photo.id, ...nonSessionPhotoIds]);

      for (const id of removedPhotoIds) {
        removedPhotoIdsRef.current.add(id);
        refreshedPhotosRef.current.delete(id);
      }
      removePhotoInfosFromSession(removedPhotoIds);
      refreshFromSession();

      return { success: true };
    },
    [photos, refreshFromSession, updateLineItems, removeLineItem, wishlist, miniCarts],
  );

  /**
   * Removes every photo: clears all of their cart and/or wishlist line items in one call
   * (only made if at least one exists), then wipes all photos from session storage.
   */
  const removeAllPhotos = useCallback(async (): Promise<RemovePhotosResult> => {
    try {
      const uniqueCartIds = [...new Set(photos.flatMap((photo) => photo.cartLineItemRefs.map((ref) => ref.cartId)))];

      const hasWishlistLineItems = Array.isArray(wishlist?.lineItems) && wishlist.lineItems.length > 0;
      const wishlistIds = wishlist?.wishlistId && hasWishlistLineItems ? [wishlist.wishlistId] : [];

      if (uniqueCartIds.length > 0 || wishlistIds.length > 0) {
        await removeAllLineItems({
          cartIds: uniqueCartIds,
          wishlistIds,
        });

        // Cart item removal can affect saved drafts, so revalidate wishlist cache as well.
        await mutate(WISHLIST_ACTION_KEY);
      }

      // Record the removals so the rehydration effect can't re-add them from a stale cache.
      const removedPhotoIds = new Set(photos.map((photo) => photo.id));
      for (const photoId of removedPhotoIds) removedPhotoIdsRef.current.add(photoId);
      refreshedPhotosRef.current.clear();
      removePhotoInfosFromSession(removedPhotoIds);
      refreshFromSession();

      return { success: true };
    } catch (error) {
      console.error('Failed to remove all photos', error);
      return { success: false };
    }
  }, [photos, refreshFromSession, removeAllLineItems, wishlist]);

  useEffect(() => {
    if (scope !== 'full' || !miniCartsLoaded) {
      return;
    }
    refreshReferencedPhotos();
  }, [scope, miniCartsLoaded, sessionVersion, refreshReferencedPhotos]);

  // Keeps every mounted useMyPhotos instance (e.g. the account page and the header widget) in
  // sync — recomputes `photos` whenever ANY component writes to session storage's customization data.
  useEffect(() => subscribeToCustomizationData(refreshFromSession), [refreshFromSession]);

  return { photos, isLoading, removePhoto, removeAllPhotos };
};

export default useMyPhotos;
